import { FC, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Typography } from '@altermaind/web/ui';
import { Charts } from '@/_shared/components/Charts';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { CallResponsePostTemplateSimulate } from '../state/api/postTemplateSimulateCall';

type Props = {
  loading: boolean;
  response?: CallResponsePostTemplateSimulate;
};

export const SimulateSurveyResults: FC<Props> = ({ loading, response }) => {
  const t = useTranslations('surveys.simulate.results.insights');
  const { genericResponsiveValue } = useResponsiveStyle();

  const simulationResults = useMemo(() => {
    const results = response?.results;
    if (!results?.length) {
      return { chartData: [], proElements: [], conElements: [] };
    }

    const sortedByScore = [...results].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    const proElements = sortedByScore.slice(0, 2);
    const conElements = sortedByScore.slice(-2).reverse();

    const chartData = results.map(item => ({
      subject: item.metric?.name ?? '',
      current: item.score ?? 0,
      expected: 7,
      description: item.metric?.description ?? '',
    }));

    return { chartData, proElements, conElements };
  }, [response]);

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' py='xl'>
        <Loader color='primary/text' size='50px' />
      </Box>
    );
  }

  return (
    <Box
      display='flex'
      flexDirection={genericResponsiveValue({
        breakPoints: { desktop: 'row' },
        defaultValue: 'column',
      })}
      mb='l'
    >
      <Box flex='2'>
        <Charts.RadarChartComponent data={simulationResults.chartData} />
      </Box>
      <Box
        flex='1'
        mt={genericResponsiveValue({
          breakPoints: { desktop: 'l' },
          defaultValue: 'm',
        })}
      >
        <Card>
          <Typography variant='h4'>{t('proTitle')}</Typography>
          <Box display='flex' flexDirection='column' mb='m' mt='xs'>
            {simulationResults.proElements.map(item => (
              <Card
                mb='xxs'
                backgroundColor='primary/inactive'
                key={item.metric?.name}
                px='xs'
                py='xxxs'
                width='fit-content'
              >
                <Typography variant='p2/light' fontSize='xs'>
                  {item.metric?.name}
                </Typography>
              </Card>
            ))}
          </Box>
          <Typography variant='h4'>{t('conTitle')}</Typography>
          <Box display='flex' flexDirection='column' mt='xs'>
            {simulationResults.conElements.map(item => (
              <Card
                mb='xxs'
                backgroundColor='error/opacity'
                key={item.metric?.name}
                px='xs'
                py='xxxs'
                width='fit-content'
              >
                <Typography variant='p2/light' fontSize='xs'>
                  {item.metric?.name}
                </Typography>
              </Card>
            ))}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
